import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Search } from 'lucide-react';
import { PaymentsYearSelector } from '../components/payments/PaymentsYearSelector';
import { PaymentsKpiBar } from '../components/payments/PaymentsKpiBar';
import { ProjectPaymentCard } from '../components/payments/ProjectPaymentCard';
import { IncomeTrend } from '../components/payments/IncomeTrend';
import { PaymentsMovementsRegistry } from '../components/payments/PaymentsMovementsRegistry';

const CURRENT_YEAR = new Date().getFullYear();

const SITE_FILTERS = [
  { key: 'tutti', label: 'Tutti' },
  { key: 'in_corso', label: 'In corso' },
  { key: 'da_fatturare', label: 'Con importi da fatturare' },
  { key: 'da_incassare', label: 'Con fatture da incassare' },
  { key: 'chiusi', label: 'Chiusi' },
];

const buildProjects = (dashboard) => {
  const map = new Map();
  const ensure = (pid, meta) => {
    const key = pid || `noproj-${meta.project_number || 'x'}`;
    if (!map.has(key)) {
      map.set(key, {
        project_id: pid || null,
        project_number: meta.project_number || '',
        client_name: meta.client_name || '',
        property_address: meta.property_address || '',
        lavori: [], arredi: [], extra_forniture: [],
      });
    }
    const p = map.get(key);
    if (!p.client_name && meta.client_name) p.client_name = meta.client_name;
    if (!p.property_address && meta.property_address) p.property_address = meta.property_address;
    return p;
  };

  (dashboard?.all_milestones || []).forEach((m) => {
    const p = ensure(m.project_id, m);
    p.lavori.push({
      id: m.id, name: m.name, percentage: m.percentage,
      amount_net: m.amount_net ?? m.amount, vat_amount: m.vat_amount ?? 0, amount_gross: m.amount_gross ?? m.amount, amount: m.amount, status: m.status,
      paid_at: m.paid_at, invoiced_at: m.invoiced_at, invoice_date: null, unlocked_at: m.unlocked_at,
      action_required: m.action_required, _sort: m.cumulative_percentage ?? m.order ?? 0,
    });
  });
  (dashboard?.arredi_rate || []).forEach((r) => {
    const p = ensure(r.project_id, r);
    p.arredi.push({
      id: r.id, name: r.label, percentage: r.percentage,
      amount_net: r.amount_net ?? r.amount, vat_amount: r.vat_amount ?? 0, amount_gross: r.amount_gross ?? r.amount, amount: r.amount, status: r.status,
      paid_at: r.paid_at, invoiced_at: null, invoice_date: r.invoice_date, unlocked_at: r.unlocked_at,
      action_required: r.action_required, _sort: r.rate_type === 'acconto' ? 0 : 1,
    });
  });
  (dashboard?.extra_forniture?.all_payments || []).forEach((e) => {
    const p = ensure(e.project_id, e);
    p.extra_forniture.push({
      id: e.id, name: e.name || e.extra_title || 'Extra', percentage: e.percentage ?? null,
      amount_net: e.amount_net ?? e.amount, vat_amount: e.vat_amount ?? 0, amount_gross: e.amount_gross ?? e.amount, amount: e.amount, status: e.status,
      paid_at: e.payment_date || e.paid_at, invoiced_at: null, invoice_date: e.invoice_date, unlocked_at: e.unlocked_at,
      action_required: e.action_required, _sort: 0,
    });
  });

  const arr = Array.from(map.values());
  arr.forEach((p) => { p.lavori.sort((a, b) => a._sort - b._sort); p.arredi.sort((a, b) => a._sort - b._sort); });
  return arr;
};

const PaymentsDashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAdmin, isAdministration } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(String(CURRENT_YEAR));
  const [search, setSearch] = useState('');
  // Deep-link dalla Dashboard Direzione: ?filter=da_fatturare | da_incassare (riusa i filtri esistenti).
  const initialFilter = SITE_FILTERS.some((f) => f.key === searchParams.get('filter'))
    ? searchParams.get('filter')
    : 'tutti';
  const [siteFilter, setSiteFilter] = useState(initialFilter);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const q = year === 'all' ? '' : `?year=${year}`;
      const res = await fetch(`/api/payments/dashboard${q}`, { credentials: 'include' });
      if (res.ok) setDashboard(await res.json());
      else toast.error('Errore nel caricamento');
    } catch {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  // Deep-link: se arriviamo con un filtro attivo, porta la sezione Cantieri in vista.
  useEffect(() => {
    if (!loading && initialFilter !== 'tutti') {
      const el = document.querySelector('[data-testid="sites-section"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading, initialFilter]);

  const projects = useMemo(() => buildProjects(dashboard), [dashboard]);

  const filteredProjects = useMemo(() => {
    let list = projects;
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((p) => (p.client_name || '').toLowerCase().includes(q) || (p.project_number || '').toLowerCase().includes(q));
    const hasStatus = (p, st) => [...p.lavori, ...p.arredi, ...p.extra_forniture].some((n) => n.status === st);
    const allNodes = (p) => [...p.lavori, ...p.arredi, ...p.extra_forniture];
    switch (siteFilter) {
      case 'in_corso': list = list.filter((p) => allNodes(p).some((n) => n.status !== 'incassato')); break;
      case 'da_fatturare': list = list.filter((p) => hasStatus(p, 'da_fatturare')); break;
      case 'da_incassare': list = list.filter((p) => hasStatus(p, 'fatturato')); break;
      case 'chiusi': list = list.filter((p) => { const n = allNodes(p); return n.length > 0 && n.every((x) => x.status === 'incassato'); }); break;
      default: break;
    }
    // §28 — ordine priorità amministrativa (solo sorting, nessuna modifica ai calcoli delle card):
    // 1. da fatturare · 2. fatturato non incassato · 3. solo non sbloccato · 4. chiusi/incassati.
    // Dentro la fascia: anzianità dell'evento rilevante (asc, più vecchio prima); fallback project_number.
    const EPOCH = 8.64e15;
    const oldestTs = (p, field) => {
      const ts = allNodes(p).filter((n) => (field === 'unlocked_at' ? n.status === 'da_fatturare' : n.status === 'fatturato'))
        .map((n) => new Date(n[field] || n.unlocked_at || 0).getTime()).filter((t) => t > 0);
      return ts.length ? Math.min(...ts) : EPOCH;
    };
    const rank = (p) => {
      // §28/§29: solo sorting (nessuna modifica ai calcoli delle card). Usa l'IMPORTO del bucket,
      // così una voce da_fatturare a 0 non precede cantieri con reale lavoro da fatturare.
      const sumBy = (st) => allNodes(p).filter((n) => n.status === st).reduce((s, n) => s + (n.amount_net ?? n.amount ?? 0), 0);
      if (sumBy('da_fatturare') > 0) return 1;
      if (sumBy('fatturato') > 0) return 2;
      if (sumBy('non_sbloccato') > 0) return 3;
      return 4;
    };
    return [...list].sort((a, b) => {
      const ra = rank(a), rb = rank(b);
      if (ra !== rb) return ra - rb;
      if (ra === 1 || ra === 2) {
        const field = ra === 1 ? 'unlocked_at' : 'invoiced_at';
        const ta = oldestTs(a, field), tb = oldestTs(b, field);
        if (ta !== tb) return ta - tb;
      }
      return (a.project_number || '').localeCompare(b.project_number || '');
    });
  }, [projects, search, siteFilter]);

  const valoreCantieri = dashboard?.iva_kpi?.totale?.net_formatted
    || (dashboard?.iva_kpi?.totale?.net != null
      ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(dashboard.iva_kpi.totale.net)
      : null);

  if (!isAdmin && !isAdministration) {
    return <div className="p-8 text-center"><p className="text-[#737373]">Accesso non autorizzato</p></div>;
  }

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-[1600px] mx-auto" data-testid="payments-v2-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-[#3C3C3C]">Pagamenti</h1>
        <PaymentsYearSelector value={year} onChange={setYear} availableYears={dashboard?.year_kpi?.available_years} />
      </div>

      {loading ? (
        <div className="p-12 text-center text-[#737373]" data-testid="payments-loading">Caricamento...</div>
      ) : (
        <>
          <PaymentsKpiBar dashboard={dashboard} year={year} />

          <section data-testid="sites-section" className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-lg font-bold text-[#3C3C3C]">
                Cantieri <span className="text-[#737373] font-normal text-sm">({filteredProjects.length})</span>
                {valoreCantieri && <span className="text-[#737373] font-normal text-sm" data-testid="sites-valore"> · Valore {valoreCantieri}</span>}
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Search className="w-4 h-4 text-[#a3a3a3] absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca cliente/progetto"
                    data-testid="sites-search" className="pl-8 h-9 w-56 text-sm" />
                </div>
                <div className="flex gap-1 flex-wrap">
                  {SITE_FILTERS.map((f) => (
                    <button key={f.key} onClick={() => setSiteFilter(f.key)} data-testid={`site-filter-${f.key}`}
                      className={`px-2.5 py-1 text-xs rounded-full border ${siteFilter === f.key ? 'bg-[#FF7819] text-white border-[#FF7819]' : 'bg-white text-[#525252] border-slate-300 hover:border-[#FF7819]'}`}>
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {filteredProjects.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-sm text-[#737373]">Nessun cantiere</CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {filteredProjects.map((p) => <ProjectPaymentCard key={p.project_id || p.project_number} project={p} />)}
              </div>
            )}
          </section>

          <IncomeTrend data={dashboard?.year_kpi?.income_by_month} year={year} />

          <PaymentsMovementsRegistry actions={dashboard?.admin_actions} />
        </>
      )}
    </div>
  );
};

export default PaymentsDashboardPage;
